import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Chapter } from "../components/Chapter";
import { Reveal, MaskedLine } from "../lib/motion";
import { usePageSeo } from "../lib/i18nContext";
import LocalizedLink from "../components/LocalizedLink";
import { SequenceLines } from "../components/Motifs";

const WRAP = "max-w-[1240px] mx-auto px-6 md:px-12 xl:px-16";
const H2 = "font-display text-[26px] md:text-[32px] font-semibold tracking-tight text-ink leading-tight";

const About = () => {
  usePageSeo("about");
  const { t } = useTranslation();
  const values = t("aboutPage.values.items", { returnObjects: true });
  const principles = t("aboutPage.s2.principles", { returnObjects: true });
  const buyers = t("aboutPage.s3.buyers", { returnObjects: true });

  return (
    <main className="pt-nav">
      <section data-testid="about-hero" className="bg-white border-b border-ice">
        <div className={`${WRAP} py-16 md:py-24`}>
          <p className="font-mono text-xs md:text-[13px] font-medium tracking-[0.18em] uppercase text-pine mb-8">{t("aboutPage.eyebrow")}</p>
          <h1 className="font-display font-semibold text-ink text-[36px] md:text-[44px] tracking-tight leading-[1.12] max-w-4xl" data-testid="about-headline">
            <MaskedLine delay={0.1}>{t("aboutPage.line1")}</MaskedLine>
            <MaskedLine delay={0.2}>{t("aboutPage.line2")}</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-white">
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
          <div className="lg:col-span-8 max-w-3xl">
            <Chapter index="01" label={t("aboutPage.s1.chapter")} />
            <Reveal delay={0.05}>
              <h2 className={`${H2} mt-8`}>{t("aboutPage.s1.heading")}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-slate leading-relaxed mt-8 text-base md:text-lg">{t("aboutPage.s1.p1")}</p>
              <p className="text-slate leading-relaxed mt-6 text-base md:text-lg">{t("aboutPage.s1.p2")}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <blockquote className="border-s-2 border-pine ps-6 md:ps-10 mt-14 max-w-2xl" data-testid="about-pull-quote">
                <p className="font-display text-[22px] md:text-[28px] font-semibold text-ink leading-snug">{t("aboutPage.s1.quote")}</p>
              </blockquote>
            </Reveal>
          </div>
          <div className="lg:col-span-4 hidden lg:flex items-end" aria-hidden="true">
            <SequenceLines rows={10} gap={20} className="w-full h-[200px]" />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-ice" data-testid="about-values-section">
        <div className={WRAP}>
          <Chapter index="02" label={t("aboutPage.values.chapter")} />
          <Reveal>
            <h2 className={`${H2} mt-8 max-w-3xl`}>{t("aboutPage.values.heading")}</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-s border-ink/10 mt-14 md:mt-20 bg-white">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="border-e border-b border-ink/10 p-8 md:p-10 h-full" data-testid={`brand-value-0${i + 1}`}>
                  <p className="font-mono text-xs font-medium tracking-[0.18em] text-pine">0{i + 1}</p>
                  <h3 className="font-display text-[22px] font-semibold text-ink mt-6">{v.title}</h3>
                  <p className="text-sm text-slate mt-3 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white" data-testid="about-principles-section">
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-10`}>
          <div className="lg:col-span-4">
            <Chapter index="03" label={t("aboutPage.s2.chapter")} />
            <Reveal>
              <p className="font-display text-lg md:text-xl font-semibold text-ink leading-snug mt-6">{t("aboutPage.s2.heading")}</p>
            </Reveal>
          </div>
          <dl className="lg:col-span-8 border-t border-ice">
            {principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.04}>
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 py-5 border-b border-ice" data-testid={`principle-0${i + 1}`}>
                  <dt className="sm:col-span-4 text-[15px] font-semibold text-ink">{p.title}</dt>
                  <dd className="sm:col-span-8 text-[15px] text-slate leading-relaxed">{p.text}</dd>
                </div>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-mist border-t border-ice">
        <div className={WRAP}>
          <Chapter index="04" label={t("aboutPage.s3.chapter")} />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-8">
            <Reveal className="lg:col-span-5">
              <h2 className={H2}>{t("aboutPage.s3.heading")}</h2>
            </Reveal>
            <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 border-t border-ice">
              {buyers.map((b) => (
                <li key={b} className="py-3 border-b border-ice text-[15px] text-ink/85">{b}</li>
              ))}
            </ul>
          </div>
          <Reveal delay={0.1}>
            <LocalizedLink
              to="/contact"
              data-testid="about-cta"
              className="inline-flex items-center gap-2 mt-16 bg-ink text-white font-semibold px-8 py-4 rounded text-sm transition-colors duration-300 hover:bg-deep"
            >
              {t("aboutPage.s3.cta")} <ArrowRight size={16} className="rtl:-scale-x-100" />
            </LocalizedLink>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default About;
